/**
 * WorkspaceEventsContext — Unified real-time event provider.
 *
 * Maintains a single SSE connection to /api/v1/workspace/events and
 * dispatches incoming events to the relevant domain contexts:
 *
 * - job.progress (semantic build) → DataModelsContext
 * - job.completed (semantic build) → DataModelsContext refresh
 * - job.progress (dashboard gen) → DashboardContext
 * - job.completed (dashboard gen) → DashboardContext refresh
 * - job.failed → Error state in relevant context
 *
 * Also exposes raw events for components that need granular access
 * (e.g., BuildProgressCard, ProgressView).
 */

import React, { createContext, useContext, useCallback, useState, useMemo, useRef } from 'react';
import { useWorkspaceEvents, WorkspaceEvent } from '../hooks/useWorkspaceEvents';
import { useAuth } from './AuthContext';
import { useDataModels } from './DataModelsContext';
import { useDashboard } from './DashboardContext';
import { useConnections } from './ConnectionsContext';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface JobProgress {
  jobId: string;
  correlationId: string;
  state: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  progress: number;
  stage: string;
  message: string;
  /** Machine-readable message ID for UI translation (e.g. LOADING_DATA) */
  messageId: string;
  steps: Array<{
    id: string;
    label: string;
    status: 'completed' | 'active' | 'queued' | 'failed';
    message?: string;
  }>;
}

export interface DataModelCreatedEvent {
  dataModelId: string;
  connectionId: string;
  name: string;
  timestamp: string;
}

interface WorkspaceEventsContextType {
  /** Whether the SSE connection is active */
  isConnected: boolean;
  /** Active job progress states keyed by jobId */
  jobProgress: Record<string, JobProgress>;
  /** Most recent event (for debugging) */
  lastEvent: WorkspaceEvent | null;
  /** Fired when auto-pipeline creates a data model. Cleared after consumption. */
  dataModelCreated: DataModelCreatedEvent | null;
  /** Clear the dataModelCreated event (after navigation) */
  clearDataModelCreated: () => void;
}

const WorkspaceEventsContext = createContext<WorkspaceEventsContextType>({
  isConnected: false,
  jobProgress: {},
  lastEvent: null,
  dataModelCreated: null,
  clearDataModelCreated: () => {},
});

// ─── Provider ─────────────────────────────────────────────────────────────────

export const WorkspaceEventsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const { loadDataModels } = useDataModels();
  const { refreshDashboards } = useDashboard();
  const { refreshConnections } = useConnections();

  const [isConnected, setIsConnected] = useState(false);
  const [jobProgress, setJobProgress] = useState<Record<string, JobProgress>>({});
  const [lastEvent, setLastEvent] = useState<WorkspaceEvent | null>(null);
  const [dataModelCreated, setDataModelCreated] = useState<DataModelCreatedEvent | null>(null);

  const clearDataModelCreated = useCallback(() => setDataModelCreated(null), []);

  // Debounce context refreshes to avoid flooding
  const refreshTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const debouncedRefresh = useCallback((key: string, fn: () => void, delayMs = 1000) => {
    if (refreshTimersRef.current[key]) {
      clearTimeout(refreshTimersRef.current[key]);
    }
    refreshTimersRef.current[key] = setTimeout(() => {
      fn();
      delete refreshTimersRef.current[key];
    }, delayMs);
  }, []);

  const handleEvent = useCallback((event: WorkspaceEvent) => {
    setLastEvent(event);

    const { type, jobId, data } = event;

    // Extract job metadata from event data
    const jobType = (data?.job_type as string) || (data?.sub_type as string) || '';
    const progress = (data?.progress as number) ?? 0;
    const stage = (data?.stage as string) || (data?.phase as string) || '';
    const message = (data?.message as string) || '';
    const messageId = (data?.message_id as string) || '';
    const steps = (data?.steps as JobProgress['steps']) || [];

    switch (type) {
      case 'job.started': {
        setJobProgress(prev => ({
          ...prev,
          [jobId]: {
            jobId,
            correlationId: event.correlationId,
            state: 'running',
            progress: 0,
            stage: stage || 'Starting',
            message: message || 'Job started',
            messageId,
            steps,
          },
        }));

        // If this is a semantic build, refresh models to pick up the 'building' status
        if (jobType === 'suggest_semantic_views' || jobType === 'semantic_build') {
          debouncedRefresh('dataModels-started', () => loadDataModels(), 500);
        }
        break;
      }

      case 'job.progress': {
        setJobProgress(prev => ({
          ...prev,
          [jobId]: {
            ...prev[jobId],
            jobId,
            correlationId: event.correlationId,
            state: 'running',
            progress: progress * 100, // Convert 0-1 to 0-100
            stage,
            message,
            messageId: messageId || prev[jobId]?.messageId || '',
            steps: steps.length > 0 ? steps : (prev[jobId]?.steps || []),
          },
        }));
        break;
      }

      case 'job.completed': {
        setJobProgress(prev => ({
          ...prev,
          [jobId]: {
            ...prev[jobId],
            jobId,
            correlationId: event.correlationId,
            state: 'completed',
            progress: 100,
            stage: 'Complete',
            message: message || 'Job completed successfully',
            messageId: '',
            steps: steps.length > 0 ? steps : (prev[jobId]?.steps || []),
          },
        }));

        // Refresh all contexts on any job completion.
        // Python events don't reliably include job_type, so we refresh
        // everything (debounced) to ensure the UI stays in sync.
        debouncedRefresh('dataModels', () => loadDataModels(), 1000);
        debouncedRefresh('dashboards', () => refreshDashboards(), 1500);
        debouncedRefresh('connections', () => refreshConnections(), 2000);
        break;
      }

      case 'job.failed': {
        setJobProgress(prev => ({
          ...prev,
          [jobId]: {
            ...prev[jobId],
            jobId,
            correlationId: event.correlationId,
            state: 'failed',
            progress: prev[jobId]?.progress ?? 0,
            stage: 'Failed',
            message: (data?.error as string) || message || 'Job failed',
            messageId: '',
            steps: steps.length > 0 ? steps : (prev[jobId]?.steps || []),
          },
        }));

        // Refresh contexts to reflect failed state
        debouncedRefresh('dataModels-fail', () => loadDataModels(), 1000);
        debouncedRefresh('connections-fail', () => refreshConnections(), 1000);
        break;
      }

      case 'job.cancelled': {
        setJobProgress(prev => ({
          ...prev,
          [jobId]: {
            ...prev[jobId],
            jobId,
            correlationId: event.correlationId,
            state: 'cancelled',
            progress: prev[jobId]?.progress ?? 0,
            stage: 'Cancelled',
            message: message || 'Job was cancelled',
            messageId: '',
            steps: [],
          },
        }));
        break;
      }

      case 'datamodel.created': {
        const dmData = data as Record<string, unknown>;
        setDataModelCreated({
          dataModelId: (dmData.dataModelId as string) || '',
          connectionId: (dmData.connectionId as string) || '',
          name: (dmData.name as string) || '',
          timestamp: event.timestamp,
        });
        // Also refresh data models list
        debouncedRefresh('dataModels-created', () => loadDataModels(), 500);
        break;
      }

      default:
        // Unknown event type — log for debugging
        break;
    }
  }, [loadDataModels, refreshDashboards, refreshConnections, debouncedRefresh]);

  // Connect SSE when authenticated
  useWorkspaceEvents({
    enabled: isAuthenticated,
    onEvent: handleEvent,
    onOpen: () => setIsConnected(true),
    onDisconnect: () => setIsConnected(false),
  });

  const value = useMemo<WorkspaceEventsContextType>(() => ({
    isConnected,
    jobProgress,
    lastEvent,
    dataModelCreated,
    clearDataModelCreated,
  }), [isConnected, jobProgress, lastEvent, dataModelCreated, clearDataModelCreated]);

  return (
    <WorkspaceEventsContext.Provider value={value}>
      {children}
    </WorkspaceEventsContext.Provider>
  );
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useWorkspaceEventsContext(): WorkspaceEventsContextType {
  return useContext(WorkspaceEventsContext);
}

/**
 * Get progress for a specific job by ID.
 * Returns null if no progress is tracked for that job.
 */
export function useJobProgress(jobId: string | null | undefined): JobProgress | null {
  const { jobProgress } = useWorkspaceEventsContext();
  if (!jobId) return null;
  return jobProgress[jobId] ?? null;
}

/**
 * Get the most recent active (running) job progress.
 * Useful for the Home page's BuildProgressCard.
 */
export function useActiveJobProgress(): JobProgress | null {
  const { jobProgress } = useWorkspaceEventsContext();
  const activeJobs = Object.values(jobProgress).filter(j => j.state === 'running');
  if (activeJobs.length === 0) return null;
  // Return the one with highest progress (most recent activity)
  return activeJobs.sort((a, b) => b.progress - a.progress)[0];
}